import {
  strapiEnsureSamplingScheduleForQuotation,
  strapiGetQuotation,
  strapiListSamplingPlansForQuotation,
} from '../../api/strapiCrm';
import type { StrapiQuotationRow } from '../../api/strapiCrm';
import { quotationUsesSampleGroups } from '../QuotationsPage/quotationWorkflow';
import { pickActiveQuotationGroupDocumentId } from './samplingSchedulePlans';

function pickOpenContractScheduleTarget(
  groups: NonNullable<StrapiQuotationRow['quotation_groups']>,
): { groupId: string; eventId: string } | null {
  const sortedGroups = [...groups];
  for (const group of sortedGroups) {
    const groupId = String(group.documentId ?? '').trim();
    if (!groupId) continue;
    const events = [...(group.quotation_recurring_events ?? [])].sort(
      (a, b) => a.event_index - b.event_index,
    );
    for (const event of events) {
      const eventId = String(event.documentId ?? '').trim();
      if (!eventId) continue;
      const jobNumber = String(event.sampling_schedule?.work_order?.job_number ?? '').trim();
      if (!jobNumber) return { groupId, eventId };
    }
  }
  return null;
}

function firstStoredContractEvent(
  groups: NonNullable<StrapiQuotationRow['quotation_groups']>,
): { groupId: string; eventId: string } | null {
  for (const group of groups) {
    const groupId = String(group.documentId ?? '').trim();
    if (!groupId) continue;
    const events = [...(group.quotation_recurring_events ?? [])].sort(
      (a, b) => a.event_index - b.event_index,
    );
    for (const event of events) {
      const eventId = String(event.documentId ?? '').trim();
      if (eventId) return { groupId, eventId };
    }
  }
  return null;
}

/** Navigasi ke detail penjadwalan sampling untuk quotation (fallback ke list). */
export async function resolveSamplingSchedulePath(
  jwt: string,
  quotation: Pick<
    StrapiQuotationRow,
    'documentId' | 'is_contract' | 'is_consultant_quote' | 'quotation_groups'
  >,
): Promise<string> {
  let groupId: string | undefined;
  let eventId: string | undefined;
  if (quotationUsesSampleGroups(quotation)) {
    let groups = quotation.quotation_groups ?? [];
    if (!groups.length || (quotation.is_contract && !groups.some((group) => group.quotation_recurring_events?.length))) {
      const detail = await strapiGetQuotation(jwt, quotation.documentId);
      groups = detail.quotation_groups ?? groups;
    }
    if (quotation.is_contract) {
      const openEvent =
        pickOpenContractScheduleTarget(groups) ?? firstStoredContractEvent(groups);
      if (openEvent) {
        groupId = openEvent.groupId;
        eventId = openEvent.eventId;
      }
    }
    if (!groupId) {
      const plans = await strapiListSamplingPlansForQuotation(jwt, quotation.documentId);
      groupId = pickActiveQuotationGroupDocumentId(groups, plans) || undefined;
    }
  }

  const schedule = await strapiEnsureSamplingScheduleForQuotation(
    jwt,
    quotation.documentId,
    groupId,
    eventId,
  );
  if (schedule?.documentId) {
    return `/sampling/schedules/${encodeURIComponent(schedule.documentId)}`;
  }
  return '/sampling/schedules';
}
